import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiMessageCircle, FiX, FiSend } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useSettings } from '../contexts/SettingsContext';

const ChatContainer = styled.div`
  position: fixed;
  bottom: ${props => props.theme.spacing.xl};
  right: ${props => props.theme.spacing.xl};
  z-index: 1000;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    bottom: ${props => props.theme.spacing.lg};
    right: ${props => props.theme.spacing.lg};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    bottom: ${props => props.theme.spacing.md};
    right: ${props => props.theme.spacing.md};
  }
`;

const ChatButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.gradient.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.theme.shadows.lg};
  cursor: pointer;
  transition: ${props => props.theme.transitions.default};
  border: none;

  &:hover {
    transform: scale(1.1);
    box-shadow: ${props => props.theme.shadows.xl};
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    font-size: ${props => props.theme.fontSizes['2xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    width: 56px;
    height: 56px;

    svg {
      font-size: ${props => props.theme.fontSizes.xl};
    }
  }
`;

const ChatWindow = styled.div`
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 350px;
  height: 450px;
  background: white;
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows['2xl']};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  opacity: ${props => props.isOpen ? 1 : 0};
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.8)'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 340px;
    height: 480px;
    max-height: calc(100vh - 120px);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: calc(100vh - 80px);
    max-height: 600px;
    border-radius: ${props => props.theme.borderRadius.xl} ${props => props.theme.borderRadius.xl} 0 0;
    margin: 0;
    transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(100%)'};
  }
`;

const ChatHeader = styled.div`
  background: ${props => props.theme.colors.gradient.primary};
  color: white;
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-size: ${props => props.theme.fontSizes.lg};
    font-weight: ${props => props.theme.fontWeights.semibold};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};

    h3 {
      font-size: ${props => props.theme.fontSizes.base};
    }
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${props => props.theme.transitions.fast};

  &:hover {
    transform: scale(1.1);
  }

  svg {
    font-size: ${props => props.theme.fontSizes.xl};
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.lg};
  overflow-y: auto;
  background: ${props => props.theme.colors.gray[100]};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md};
    gap: ${props => props.theme.spacing.sm};
  }
`;

const Message = styled.div`
  max-width: 80%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => props.isUser ? props.theme.colors.gradient.primary : 'white'};
  color: ${props => props.isUser ? 'white' : props.theme.colors.dark};
  align-self: ${props => props.isUser ? 'flex-end' : 'flex-start'};
  box-shadow: ${props => props.theme.shadows.sm};
  word-wrap: break-word;

  p {
    margin: 0;
    font-size: ${props => props.theme.fontSizes.sm};
    line-height: 1.5;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    max-width: 85%;
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};

    p {
      font-size: ${props => props.theme.fontSizes.xs};
    }
  }
`;

const ChatInputContainer = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.gray[300]};
  background: white;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md};
  }
`;

const ChatInputWrapper = styled.form`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const ChatInput = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border: 2px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  transition: ${props => props.theme.transitions.default};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder {
    color: ${props => props.theme.colors.gray[500]};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    font-size: ${props => props.theme.fontSizes.xs};
  }
`;

const SendButton = styled.button`
  background: ${props => props.theme.colors.gradient.primary};
  color: white;
  padding: ${props => props.theme.spacing.md};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${props => props.theme.transitions.default};
  min-width: 44px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: ${props => props.theme.fontSizes.lg};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.sm};
    min-width: 40px;

    svg {
      font-size: ${props => props.theme.fontSizes.base};
    }
  }
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.gray[600]};

  h4 {
    margin: 0 0 ${props => props.theme.spacing.sm} 0;
    font-size: ${props => props.theme.fontSizes.lg};
    color: ${props => props.theme.colors.dark};
  }

  p {
    margin: 0;
    font-size: ${props => props.theme.fontSizes.sm};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.lg};

    h4 {
      font-size: ${props => props.theme.fontSizes.base};
    }

    p {
      font-size: ${props => props.theme.fontSizes.xs};
    }
  }
`;

const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  background: #25D366;
  color: white;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  text-decoration: none;
  font-weight: ${props => props.theme.fontWeights.semibold};
  font-size: ${props => props.theme.fontSizes.xs};
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.2);
  margin-top: ${props => props.theme.spacing.sm};

  &:hover {
    background: #20BA5A;
    box-shadow: 0 4px 8px rgba(37, 211, 102, 0.3);
    transform: scale(1.02);
  }

  &:active {
    transform: scale(0.98);
    box-shadow: 0 1px 2px rgba(37, 211, 102, 0.2);
  }

  svg {
    font-size: ${props => props.theme.fontSizes.lg};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
    font-size: ${props => props.theme.fontSizes['2xs']};

    svg {
      font-size: ${props => props.theme.fontSizes.base};
    }
  }
`;

const ChatBox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: 'Hello! How can we help you today?',
      isUser: false,
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [messageIdCounter, setMessageIdCounter] = useState(2);
  const timeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const { settings } = useSettings();

  const INITIAL_MESSAGE_COUNT = 1;

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (inputValue.trim() === '') return;

    // Clear any pending timeout to prevent multiple auto-responses
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Add user message
    const userMessage = {
      id: messageIdCounter,
      text: inputValue,
      isUser: true,
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    const currentCounterValue = messageIdCounter;
    setMessageIdCounter(prev => prev + 1);
    setInputValue('');

    // Simulate auto-response after a short delay
    timeoutRef.current = setTimeout(() => {
      const formattedPhone = settings?.phone?.replace(/[^0-9]/g, '') || '';
      const whatsappMessage = encodeURIComponent(inputValue);
      const whatsappUrl = `https://wa.me/${formattedPhone}?text=${whatsappMessage}`;
      
      const autoResponse = {
        id: currentCounterValue + 1,
        text: 'Thank you for contacting us! For faster response, please click the button below to chat with us on WhatsApp.',
        isUser: false,
        hasWhatsApp: true,
        whatsappUrl: whatsappUrl,
      };
      setMessages(prevMessages => [...prevMessages, autoResponse]);
      setMessageIdCounter(prev => prev + 1);
    }, 1000);
  };

  return (
    <ChatContainer>
      <ChatWindow isOpen={isOpen}>
        <ChatHeader>
          <h3>Chat with us</h3>
          <CloseButton onClick={toggleChat}>
            <FiX />
          </CloseButton>
        </ChatHeader>

        <ChatMessages>
          {messages.length === INITIAL_MESSAGE_COUNT ? (
            <WelcomeMessage>
              <h4>👋 Welcome!</h4>
              <p>How can we assist you today?</p>
            </WelcomeMessage>
          ) : (
            messages.map(message => (
              <Message key={message.id} isUser={message.isUser}>
                <p>{message.text}</p>
                {message.hasWhatsApp && message.whatsappUrl && settings?.phone && (
                  <WhatsAppButton href={message.whatsappUrl} target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp />
                    Chat on WhatsApp
                  </WhatsAppButton>
                )}
              </Message>
            ))
          )}
          <div ref={messagesEndRef} />
        </ChatMessages>

        <ChatInputContainer>
          <ChatInputWrapper onSubmit={handleSubmit}>
            <ChatInput
              type="text"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <SendButton type="submit">
              <FiSend />
            </SendButton>
          </ChatInputWrapper>
        </ChatInputContainer>
      </ChatWindow>

      <ChatButton onClick={toggleChat}>
        {isOpen ? <FiX /> : <FiMessageCircle />}
      </ChatButton>
    </ChatContainer>
  );
};

export default ChatBox;
